import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import './SchoolDetail.css'

const STATUSES = ['Researching', 'Contacted', 'Visited', 'Offer Received', 'Committed']

function haversine(lat1, lng1, lat2, lng2) {
  const R    = 3958.8
  const toRad = d => d * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a    = Math.sin(dLat / 2) ** 2
            + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return Math.round(R * 2 * Math.asin(Math.sqrt(a)))
}

const MY_INTEREST_OPTIONS = ['Dream School', 'High Interest', 'Interest']

const CONTACT_LEVEL_OPTIONS = [
  'No Official Contact',
  'Camp Invite',
  'Coach Response - Email',
  'Coach Response - Text',
  'Coach Response - Call',
  'Third Party Contact',
  'In-Person Contact',
]

const STATUS_META = {
  Researching:      { className: 'status-researching' },
  Contacted:        { className: 'status-contacted' },
  Visited:          { className: 'status-visited' },
  'Offer Received': { className: 'status-offer' },
  Committed:        { className: 'status-committed' },
}

const COACH_TITLES = [
  'Head Coach',
  'Associate Head Coach',
  'Assistant Coach',
  'Pitching Coach',
  'Hitting Coach',
  'Defensive Coach',
  'Bullpen Coach',
  'Volunteer Assistant',
  'Recruiting Coordinator',
  'Director of Player Personnel',
  'Director of Operations',
  'Director of Player Development',
  'Strength & Conditioning Coach',
  'Athletic Trainer',
  'Graduate Assistant',
]

const EMPTY_COACH_FORM = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  phone: '',
  instagram: '',
  twitter: '',
}


function initials(first, last) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase()
}

export default function SchoolDetail({ pipelineSchool, user, onBack }) {
  const [dbInfo, setDbInfo]       = useState(null)
  const [dbLoading, setDbLoading] = useState(true)

  const [status, setStatus]             = useState(pipelineSchool.status)
  const [myInterest, setMyInterest]     = useState(pipelineSchool.my_interest || '')
  const [contactLevel, setContactLevel] = useState(pipelineSchool.contact_level || '')
  const [notes, setNotes]               = useState(pipelineSchool.notes || '')
  const notesTimerRef                   = useRef(null)

  const [verifiedCoaches, setVerifiedCoaches]     = useState([])
  const [coaches, setCoaches]                     = useState([])
  const [coachesLoading, setCoachesLoading]       = useState(true)
  const [deletingId, setDeletingId]               = useState(null)
  const [submittedCoachIds, setSubmittedCoachIds] = useState(new Set())
  const [submittingId, setSubmittingId]           = useState(null)

  // "Add Coach" form
  const [showAddForm, setShowAddForm] = useState(false)
  const [coachForm, setCoachForm]     = useState(EMPTY_COACH_FORM)
  const [coachSaving, setCoachSaving] = useState(false)
  const [coachError, setCoachError]   = useState(null)

  // Modals
  const [suggestEditTarget, setSuggestEditTarget] = useState(null) // coaches_db coach → form modal
  const [suggestNoteTarget, setSuggestNoteTarget] = useState(null) // schools_db HC → note modal

  // Toast
  const [toast, setToast] = useState(null)

  // Distance
  const [userLat, setUserLat] = useState(null)  // null = not yet loaded
  const [userLng, setUserLng] = useState(null)
  const [coordsLoaded, setCoordsLoaded] = useState(false)

  useEffect(() => {
    async function loadUserCoords() {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      console.log('[SchoolDetail] home_lat:', profile?.home_lat, 'home_lng:', profile?.home_lng)
      setUserLat(profile?.home_lat ? parseFloat(profile.home_lat) : null)
      setUserLng(profile?.home_lng ? parseFloat(profile.home_lng) : null)
      setCoordsLoaded(true)
    }
    loadUserCoords()
  }, [user.id])

  useEffect(() => {
    if (pipelineSchool.school_name) {
      document.title = `${pipelineSchool.school_name} — CommitHQ`
    }
  }, [pipelineSchool.school_name])

  useEffect(() => {
    async function fetchDbInfo() {
      const { data } = await supabase
        .from('schools_db')
        .select('id, conference, state, head_coach, lat, lng')
        .ilike('school_name', pipelineSchool.school_name)
        .limit(1)
        .single()
      setDbInfo(data || null)
      setDbLoading(false)
    }
    fetchDbInfo()
  }, [pipelineSchool.school_name])

  const fetchCoaches = useCallback(async () => {
    setCoachesLoading(true)
    const [coachRes, pendingRes, verifiedRes] = await Promise.all([
      supabase
        .from('coaches')
        .select('*')
        .eq('user_id', user.id)
        .eq('school_name', pipelineSchool.school_name)
        .order('created_at', { ascending: true }),
      supabase
        .from('coaches_pending')
        .select('coach_id')
        .eq('submitted_by', user.id)
        .eq('school_name', pipelineSchool.school_name),
      supabase
        .from('coaches_db')
        .select('*')
        .eq('school_name', pipelineSchool.school_name)
        .order('approved_at', { ascending: true }),
    ])
    const coachesList  = coachRes.data     || []
    const pendingRows  = pendingRes.data   || []
    const verifiedList = verifiedRes.data  || []

    const validCoachIds = new Set(pendingRows.map(r => r.coach_id).filter(id => id != null))

    setVerifiedCoaches(verifiedList)
    setCoaches(coachesList)
    setSubmittedCoachIds(validCoachIds)
    setCoachesLoading(false)
  }, [user.id, pipelineSchool.school_name])

  useEffect(() => { fetchCoaches() }, [fetchCoaches])

  // ── Status / Notes ──────────────────────────────────────────────

  async function handleStatusChange(e) {
    const value = e.target.value
    setStatus(value)
    await supabase.from('pipeline_schools').update({ status: value }).eq('id', pipelineSchool.id)
  }

  function handleNotesChange(e) {
    const value = e.target.value
    setNotes(value)
    clearTimeout(notesTimerRef.current)
    notesTimerRef.current = setTimeout(() => {
      supabase.from('pipeline_schools').update({ notes: value }).eq('id', pipelineSchool.id)
    }, 1000)
  }

  async function handleMyInterestChange(e) {
    const value = e.target.value
    setMyInterest(value)
    await supabase
      .from('pipeline_schools')
      .update({ my_interest: value || null })
      .eq('id', pipelineSchool.id)
  }

  async function handleContactLevelChange(e) {
    const value = e.target.value
    setContactLevel(value)
    await supabase
      .from('pipeline_schools')
      .update({ contact_level: value || null })
      .eq('id', pipelineSchool.id)
  }

  // ── Add coach form ───────────────────────────────────────────────

  function openAddForm() { setCoachForm(EMPTY_COACH_FORM); setCoachError(null); setShowAddForm(true) }
  function cancelAddForm() { setShowAddForm(false); setCoachError(null) }

  function handleCoachFormChange(e) {
    setCoachForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleAddCoach(e) {
    e.preventDefault()
    const missing = []
    if (!coachForm.first_name.trim()) missing.push('First Name')
    if (!coachForm.last_name.trim())  missing.push('Last Name')
    if (!coachForm.title)             missing.push('Title')
    if (!coachForm.email.trim())      missing.push('Email')
    if (missing.length) { setCoachError(`Required: ${missing.join(', ')}`); return }
    if (!user?.id) { setCoachError('No authenticated user. Please sign out and sign back in.'); return }
    setCoachSaving(true); setCoachError(null)
    const { data: newCoach, error } = await supabase
      .from('coaches')
      .insert({
        user_id:     user.id,
        school_name: pipelineSchool.school_name,
        first_name:  coachForm.first_name.trim(),
        last_name:   coachForm.last_name.trim(),
        title:       coachForm.title.trim(),
        email:       coachForm.email.trim(),
        phone:       coachForm.phone.trim(),
        instagram:   coachForm.instagram.trim(),
        twitter:     coachForm.twitter.trim(),
      })
      .select()
      .single()
    if (error) { setCoachError(error.message) }
    else { setCoaches(prev => [...prev, newCoach]); setShowAddForm(false) }
    setCoachSaving(false)
  }

  async function handleSubmitToDb(coach) {
    setSubmittingId(coach.id)
    const payload = {
      submitted_by: user.id,
      coach_id:     coach.id,
      school_name:  coach.school_name,
      division:     pipelineSchool.division,
      first_name:   coach.first_name,
      last_name:    coach.last_name,
      title:        coach.title,
      email:        coach.email,
      phone:        coach.phone,
      instagram:    coach.instagram,
      twitter:      coach.twitter,
      status:       'pending',
    }
    const { error } = await supabase.from('coaches_pending').insert(payload)
    if (!error) {
      setSubmittedCoachIds(prev => new Set([...prev, coach.id]))
      console.log('[Notify] coach object:', coach)
      console.log('[Notify] user object:', user)
      console.log('[Notify] Invoking with:', {
        coach:       `${coach.first_name} ${coach.last_name}`,
        school:      pipelineSchool.school_name,
        submittedBy: user.email,
      })
      supabase.functions.invoke('notify-coach-submitted', {
        body: JSON.stringify({
          coach:            `${coach.first_name} ${coach.last_name}`,
          school:           pipelineSchool.school_name,
          submittedBy:      user.email,
          submittedByEmail: user.email,
        }),
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      })
    }
    setSubmittingId(null)
  }

  function handleUpdateCoach(updatedCoach) {
    setCoaches(prev => prev.map(c => c.id === updatedCoach.id ? updatedCoach : c))
  }

  async function handleDeleteCoach(id) {
    setDeletingId(id)
    await supabase.from('coaches').delete().eq('id', id)
    setCoaches(prev => prev.filter(c => c.id !== id))
    setDeletingId(null)
  }

  // ── Derived ─────────────────────────────────────────────────────

  const bothLoaded      = !coachesLoading && !dbLoading
  const verifiedEmails  = new Set(verifiedCoaches.map(v => v.email?.toLowerCase()).filter(Boolean))
  const verifiedHaHC    = verifiedCoaches.some(v => v.title?.toLowerCase() === 'head coach')
  const showDbHeadCoach = bothLoaded && !!dbInfo?.head_coach && !verifiedHaHC

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="school-detail">
      <div className="detail-topbar">
        <button className="detail-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Pipeline
        </button>
      </div>

      <div className="detail-header">
        <h1 className="detail-school-name">{pipelineSchool.school_name}</h1>
      </div>

      {/* ── Program Stats Strip ── */}
      {!dbLoading && (
        <div className="program-stats-strip">
          {pipelineSchool.division && (
            <div className={`stats-strip-item stats-strip-div stats-div-${pipelineSchool.division.toLowerCase()}`}>
              {pipelineSchool.division}
            </div>
          )}
          {dbInfo?.conference && (
            <>
              <div className="stats-strip-sep" />
              <div className="stats-strip-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                {dbInfo.conference}
              </div>
            </>
          )}
          {dbInfo?.state && (
            <>
              <div className="stats-strip-sep" />
              <div className="stats-strip-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {dbInfo.state}
              </div>
            </>
          )}
        </div>
      )}

      <div className="detail-body">
        <div className="detail-two-col">
          <div className="detail-left-col">
          <section className="detail-section">
            <h2 className="section-title">Recruiting Status</h2>
            <div className="detail-field">
              <label htmlFor="detail-status">Status</label>
              <div className="status-select-wrap">
                <select
                  id="detail-status"
                  value={status}
                  onChange={handleStatusChange}
                  className={`status-select ${STATUS_META[status]?.className ?? ''}`}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="detail-field">
              <label htmlFor="detail-my-interest">My Interest</label>
              <div className="status-select-wrap">
                <select
                  id="detail-my-interest"
                  value={myInterest}
                  onChange={handleMyInterestChange}
                  className="status-select"
                >
                  <option value="">—</option>
                  {MY_INTEREST_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="detail-field">
              <label htmlFor="detail-contact-level">Contact Level</label>
              <div className="status-select-wrap">
                <select
                  id="detail-contact-level"
                  value={contactLevel}
                  onChange={handleContactLevelChange}
                  className="status-select"
                >
                  <option value="">—</option>
                  {CONTACT_LEVEL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* ── Map + Distance ── */}
          {dbInfo?.state && (
            <section className="detail-section detail-section-map">
              <h2 className="section-title">Location</h2>
              <div className="map-embed-wrap">
                <iframe
                  title="School Map"
                  className="map-embed"
                  src={`https://maps.google.com/maps?q=${[pipelineSchool.school_name, dbInfo.state].join(' ').replace(/\s+/g, '+')}&output=embed`}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              {coordsLoaded && (
                userLat && userLng && dbInfo?.lat && dbInfo?.lng
                  ? <p className="map-distance">📍 {haversine(userLat, userLng, parseFloat(dbInfo.lat), parseFloat(dbInfo.lng)).toLocaleString()} miles from home</p>
                  : <p className="map-distance-hint">Add your zip code in Settings to see distance</p>
              )}
            </section>
          )}
          </div>

          <div className="detail-right-col">
          {/* ── Coaches ── */}
          <section className="detail-section">
          <div className="coaches-header">
            <h2 className="section-title" style={{ margin: 0 }}>Coaches</h2>
            {!showAddForm && (
              <button className="btn-add-coach" onClick={openAddForm}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Coach
              </button>
            )}
          </div>

          {!bothLoaded ? (
            <p className="coaches-loading">Loading coaches…</p>
          ) : (
            <>
              {/* ── Verified coaches from coaches_db ── */}
              {verifiedCoaches.length > 0 && (
                <div className="coach-cards" style={{ marginBottom: 12 }}>
                  {verifiedCoaches.map(coach => (
                    <VerifiedCoachCard
                      key={coach.id}
                      coach={coach}
                      onSuggestEdit={() => setSuggestEditTarget(coach)}
                    />
                  ))}
                </div>
              )}

              {/* ── Head coach string from schools_db ── */}
              {showDbHeadCoach && (
                <div className="coach-cards" style={{ marginBottom: 12 }}>
                  <DbHeadCoachCard
                    name={dbInfo.head_coach}
                    schoolName={pipelineSchool.school_name}
                    onSuggestNote={(coachData) => setSuggestNoteTarget(coachData)}
                  />
                </div>
              )}

              {/* ── User's personal coaches (duplicates hidden) ── */}
              {coaches.filter(c => !c.email || !verifiedEmails.has(c.email.toLowerCase())).length > 0 && (
                <div className="coach-cards">
                  {coaches
                    .filter(c => !c.email || !verifiedEmails.has(c.email.toLowerCase()))
                    .map(coach => (
                      <CoachCard
                        key={coach.id}
                        coach={coach}
                        onDelete={handleDeleteCoach}
                        deleting={deletingId === coach.id}
                        onSubmitToDb={handleSubmitToDb}
                        isSubmitted={submittedCoachIds.has(coach.id)}
                        submitting={submittingId === coach.id}
                        inVerifiedDb={false}
                        onUpdate={handleUpdateCoach}
                      />
                    ))}
                </div>
              )}

              {/* ── Empty state ── */}
              {coaches.length === 0 && !showAddForm && (
                <div className="coaches-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="7" r="3"/>
                    <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    <path d="M21 21v-2a4 4 0 0 0-3-3.85"/>
                  </svg>
                  <p>No coaches added yet.</p>
                </div>
              )}

              {/* ── Add coach form ── */}
              {showAddForm && (
                <CoachForm
                  form={coachForm}
                  onChange={handleCoachFormChange}
                  onSubmit={handleAddCoach}
                  onCancel={cancelAddForm}
                  saving={coachSaving}
                  error={coachError}
                  submitLabel="Save Coach"
                />
              )}
            </>
          )}
        </section>

          {/* ── Notes ── */}
          <section className="detail-section">
            <h2 className="section-title">Notes</h2>
            <div className="detail-field">
              <textarea
                id="detail-notes"
                value={notes}
                onChange={handleNotesChange}
                placeholder="Add notes about this school…"
                rows={5}
              />
            </div>
          </section>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {suggestEditTarget && (
        <SuggestEditModal
          coach={suggestEditTarget}
          user={user}
          pipelineSchool={pipelineSchool}
          onClose={() => setSuggestEditTarget(null)}
          onSuccess={() => {
            setSuggestEditTarget(null)
            setToast('Edit submitted for review. Thanks!')
          }}
        />
      )}

      {suggestNoteTarget && (
        <SuggestNoteModal
          coach={suggestNoteTarget}
          user={user}
          pipelineSchool={pipelineSchool}
          onClose={() => setSuggestNoteTarget(null)}
          onSuccess={() => {
            setSuggestNoteTarget(null)
            setToast('Edit submitted for review. Thanks!')
          }}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────

function DbHeadCoachCard({ name, schoolName, onSuggestNote }) {
  const parts      = (name || '').trim().split(/\s+/)
  const first_name = parts[0] || ''
  const last_name  = parts.slice(1).join(' ') || ''

  return (
    <div className="coach-card coach-card-db">
      <div className="coach-card-main">
        <div className="coach-avatar">
          {initials(first_name, last_name) || '?'}
        </div>
        <div className="coach-info">
          <div className="coach-name-row">
            <span className="coach-name">{name}</span>
            <span className="hc-badge">HC</span>
          </div>
          <span className="coach-title">Head Coach</span>
          <span className="coach-source-label">CommitHQ database</span>
        </div>
      </div>
      <div className="coach-card-actions">
        <button className="btn-suggest-edit" onClick={() => onSuggestNote({ first_name, last_name, title: 'Head Coach', email: '', phone: '', instagram: '', twitter: '', school_name: schoolName })}>
          Suggest Edit
        </button>
      </div>
    </div>
  )
}

function VerifiedCoachCard({ coach, onSuggestEdit }) {
  return (
    <div className="coach-card coach-card-verified">
      <div className="coach-card-main">
        <div className="coach-avatar coach-avatar-verified">
          {initials(coach.first_name, coach.last_name) || '?'}
        </div>
        <div className="coach-info">
          <div className="coach-name-row">
            <span className="coach-name">{coach.first_name} {coach.last_name}</span>
            <span className="verified-badge">Verified ✓</span>
          </div>
          {coach.title && <span className="coach-title">{coach.title}</span>}
          <div className="coach-contact">
            {coach.email && (
              <span className="coach-contact-email-row">
                <a href={`mailto:${coach.email}`} className="coach-contact-link">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {coach.email}
                </a>
                {coach.email_verified === false && (
                  <span className="predicted-badge">Predicted</span>
                )}
              </span>
            )}
            {coach.phone && (
              <a href={`tel:${coach.phone}`} className="coach-contact-link">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.42 2 2 0 0 1 3.62 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16z"/>
                </svg>
                {coach.phone}
              </a>
            )}
            {coach.instagram && (
              <a href={`https://instagram.com/${coach.instagram.replace(/^@/, '')}`} className="coach-contact-link coach-contact-instagram" target="_blank" rel="noreferrer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5"/>
                  <circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                </svg>
                @{coach.instagram.replace(/^@/, '')}
              </a>
            )}
            {coach.twitter && (
              <a href={`https://x.com/${coach.twitter.replace(/^@/, '')}`} className="coach-contact-link coach-contact-twitter" target="_blank" rel="noreferrer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                @{coach.twitter.replace(/^@/, '')}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="coach-card-actions">
        <button className="btn-suggest-edit" onClick={onSuggestEdit}>
          Suggest Edit
        </button>
      </div>
    </div>
  )
}

function CoachCard({ coach, onDelete, deleting, onSubmitToDb, isSubmitted, submitting, inVerifiedDb, onUpdate }) {
  const [isEditing, setIsEditing]   = useState(false)
  const [editForm, setEditForm]     = useState(null)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError]   = useState(null)

  function startEdit() {
    setEditForm({
      first_name: coach.first_name || '',
      last_name:  coach.last_name  || '',
      title:      coach.title      || '',
      email:      coach.email      || '',
      phone:      coach.phone      || '',
      instagram:  coach.instagram  || '',
      twitter:    coach.twitter    || '',
    })
    setEditError(null)
    setIsEditing(true)
  }

  function cancelEdit() { setIsEditing(false); setEditError(null) }

  function handleEditChange(e) {
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleEditSubmit(e) {
    e.preventDefault()
    const missing = []
    if (!editForm.first_name.trim()) missing.push('First Name')
    if (!editForm.last_name.trim())  missing.push('Last Name')
    if (!editForm.title)             missing.push('Title')
    if (!editForm.email.trim())      missing.push('Email')
    if (missing.length) { setEditError(`Required: ${missing.join(', ')}`); return }
    setEditSaving(true); setEditError(null)
    const { data: updated, error } = await supabase
      .from('coaches')
      .update({
        first_name: editForm.first_name.trim(),
        last_name:  editForm.last_name.trim(),
        title:      editForm.title.trim(),
        email:      editForm.email.trim(),
        phone:      editForm.phone.trim(),
        instagram:  editForm.instagram.trim(),
        twitter:    editForm.twitter.trim(),
      })
      .eq('id', coach.id)
      .select()
      .single()
    if (error) { setEditError(error.message) }
    else { onUpdate(updated); setIsEditing(false) }
    setEditSaving(false)
  }

  if (isEditing) {
    return (
      <div className="coach-card coach-card-editing">
        <CoachForm
          form={editForm}
          onChange={handleEditChange}
          onSubmit={handleEditSubmit}
          onCancel={cancelEdit}
          saving={editSaving}
          error={editError}
          submitLabel="Save Changes"
        />
      </div>
    )
  }

  return (
    <div className="coach-card">
      <div className="coach-card-main">
        <div className="coach-avatar">
          {initials(coach.first_name, coach.last_name) || '?'}
        </div>
        <div className="coach-info">
          <span className="coach-name">{coach.first_name} {coach.last_name}</span>
          {coach.title && <span className="coach-title">{coach.title}</span>}
          <div className="coach-contact">
            {coach.email && (
              <a href={`mailto:${coach.email}`} className="coach-contact-link">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                {coach.email}
              </a>
            )}
            {coach.phone && (
              <a href={`tel:${coach.phone}`} className="coach-contact-link">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.65 3.42 2 2 0 0 1 3.62 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.84a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.73 16z"/>
                </svg>
                {coach.phone}
              </a>
            )}
            {coach.instagram && (
              <a href={`https://instagram.com/${coach.instagram.replace(/^@/, '')}`} className="coach-contact-link coach-contact-instagram" target="_blank" rel="noreferrer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5"/>
                  <circle cx="12" cy="12" r="4"/>
                  <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                </svg>
                @{coach.instagram.replace(/^@/, '')}
              </a>
            )}
            {coach.twitter && (
              <a href={`https://x.com/${coach.twitter.replace(/^@/, '')}`} className="coach-contact-link coach-contact-twitter" target="_blank" rel="noreferrer">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                @{coach.twitter.replace(/^@/, '')}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="coach-card-actions">
        {!inVerifiedDb && (
          <button
            className={`btn-submit-db${isSubmitted ? ' submitted' : ''}`}
            onClick={() => onSubmitToDb(coach)}
            disabled={isSubmitted || submitting}
          >
            {isSubmitted ? 'Submitted ✓' : submitting ? 'Submitting…' : 'Submit to CommitHQ DB'}
          </button>
        )}
        <button
          className="coach-edit"
          onClick={startEdit}
          aria-label="Edit coach"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button
          className="coach-delete"
          onClick={() => onDelete(coach.id)}
          disabled={deleting}
          aria-label="Delete coach"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6"/><path d="M14 11v6"/>
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
          </svg>
        </button>
      </div>
    </div>
  )
}

function CoachForm({ form, onChange, onSubmit, onCancel, saving, error, submitLabel }) {
  return (
    <form className="coach-form" onSubmit={onSubmit}>
      <div className="coach-form-row">
        <div className="coach-form-field">
          <label>First Name <span className="required">*</span></label>
          <input name="first_name" type="text" value={form.first_name} onChange={onChange} autoFocus />
        </div>
        <div className="coach-form-field">
          <label>Last Name <span className="required">*</span></label>
          <input name="last_name" type="text" value={form.last_name} onChange={onChange} />
        </div>
        <div className="coach-form-field">
          <label>Title <span className="required">*</span></label>
          <select name="title" value={form.title} onChange={onChange}>
            <option value="">— Select title —</option>
            {COACH_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="coach-form-row">
        <div className="coach-form-field">
          <label>Email <span className="required">*</span></label>
          <input name="email" type="email" placeholder="coach@school.edu" value={form.email} onChange={onChange} />
        </div>
        <div className="coach-form-field">
          <label>Phone</label>
          <input name="phone" type="tel" placeholder="(555) 000-0000" value={form.phone} onChange={onChange} />
        </div>
      </div>
      <div className="coach-form-row coach-form-row-2">
        <div className="coach-form-field">
          <label>Instagram</label>
          <input name="instagram" type="text" placeholder="@handle" value={form.instagram} onChange={onChange} />
        </div>
        <div className="coach-form-field">
          <label>Twitter / X</label>
          <input name="twitter" type="text" placeholder="@handle" value={form.twitter} onChange={onChange} />
        </div>
      </div>
      {error && <div className="detail-error">{error}</div>}
      <div className="coach-form-actions">
        <button type="button" className="btn-cancel-coach" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn-save-coach" disabled={saving}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}

// ── Suggest Edit modal — coaches_db coaches (form-based) ─────────

const SUGGEST_EDIT_FIELDS = [
  { key: 'first_name', label: 'First Name', type: 'text',  required: true },
  { key: 'last_name',  label: 'Last Name',  type: 'text',  required: true },
  { key: 'title',      label: 'Title',      type: 'select',required: true },
  { key: 'email',      label: 'Email',      type: 'email', required: false },
  { key: 'phone',      label: 'Phone',      type: 'tel',   required: false },
  { key: 'instagram',  label: 'Instagram',  type: 'text',  required: false },
  { key: 'twitter',    label: 'Twitter / X',type: 'text',  required: false },
]

function SuggestEditModal({ coach, user, pipelineSchool, onClose, onSuccess }) {
  const [form, setForm]         = useState({
    first_name: coach.first_name || '',
    last_name:  coach.last_name  || '',
    title:      coach.title      || '',
    email:      coach.email      || '',
    phone:      coach.phone      || '',
    instagram:  coach.instagram  || '',
    twitter:    coach.twitter    || '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const missing = []
    if (!form.first_name.trim()) missing.push('First Name')
    if (!form.last_name.trim())  missing.push('Last Name')
    if (!form.title)             missing.push('Title')
    if (missing.length) { setError(`Required: ${missing.join(', ')}`); return }
    setSubmitting(true); setError(null)
    const { error: err } = await supabase.from('coaches_edits_pending').insert({
      submitted_by:   user.id,
      coaches_db_id:  coach.id,
      school_name:    coach.school_name || pipelineSchool.school_name,
      first_name:     form.first_name.trim(),
      last_name:      form.last_name.trim(),
      title:          form.title,
      email:          form.email.trim(),
      phone:          form.phone.trim(),
      instagram:      form.instagram.trim(),
      twitter:        form.twitter.trim(),
      status:         'pending',
    })
    if (err) { setError(err.message) }
    else {
      const notifyPayload = {
        coach:            `${form.first_name.trim()} ${form.last_name.trim()}`,
        school:           pipelineSchool.school_name,
        submittedBy:      user.email,
        submittedByEmail: user.email,
      }
      console.log('[Notify] notify-coach-edit payload:', notifyPayload)
      supabase.functions.invoke('notify-coach-edit', {
        body: JSON.stringify(notifyPayload),
      })
      onSuccess()
    }
    setSubmitting(false)
  }

  return (
    <div className="suggest-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="suggest-modal">
        <div className="suggest-modal-header">
          <div>
            <h3 className="suggest-modal-title">Suggest an Edit</h3>
            <p className="suggest-modal-source">{coach.first_name} {coach.last_name} · {pipelineSchool.school_name}</p>
          </div>
          <button className="suggest-modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form className="suggest-modal-form" onSubmit={handleSubmit}>
          <div className="coach-form-row">
            <div className="coach-form-field">
              <label>First Name <span className="required">*</span></label>
              <input name="first_name" type="text" value={form.first_name} onChange={handleChange} autoFocus />
            </div>
            <div className="coach-form-field">
              <label>Last Name <span className="required">*</span></label>
              <input name="last_name" type="text" value={form.last_name} onChange={handleChange} />
            </div>
            <div className="coach-form-field">
              <label>Title <span className="required">*</span></label>
              <select name="title" value={form.title} onChange={handleChange}>
                <option value="">— Select title —</option>
                {COACH_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="coach-form-row">
            <div className="coach-form-field">
              <label>Email</label>
              <input name="email" type="email" placeholder="coach@school.edu" value={form.email} onChange={handleChange} />
            </div>
            <div className="coach-form-field">
              <label>Phone</label>
              <input name="phone" type="tel" placeholder="(555) 000-0000" value={form.phone} onChange={handleChange} />
            </div>
          </div>
          <div className="coach-form-row coach-form-row-2">
            <div className="coach-form-field">
              <label>Instagram</label>
              <input name="instagram" type="text" placeholder="@handle" value={form.instagram} onChange={handleChange} />
            </div>
            <div className="coach-form-field">
              <label>Twitter / X</label>
              <input name="twitter" type="text" placeholder="@handle" value={form.twitter} onChange={handleChange} />
            </div>
          </div>

          {error && <div className="detail-error">{error}</div>}

          <div className="suggest-modal-actions">
            <button type="button" className="btn-cancel-coach" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save-coach" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit suggestion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Suggest Note modal — schools_db HC coaches (textarea-based) ──

function SuggestNoteModal({ coach, user, pipelineSchool, onClose, onSuccess }) {
  const [editNotes, setEditNotes]   = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  async function handleSubmit() {
    if (!editNotes.trim()) { setError('Please describe what needs to be updated.'); return }
    setSubmitting(true); setError(null)
    const { error: err } = await supabase.from('coaches_pending').insert({
      submitted_by:    user.id,
      submission_type: 'edit',
      edit_notes:      editNotes.trim(),
      original_source: 'schools_db',
      school_name:     coach.school_name || pipelineSchool.school_name,
      division:        pipelineSchool.division,
      first_name:      coach.first_name || '',
      last_name:       coach.last_name  || '',
      title:           coach.title      || '',
      email:           coach.email      || '',
      phone:           coach.phone      || '',
      instagram:       coach.instagram  || '',
      twitter:         coach.twitter    || '',
      status:          'pending',
    })
    if (err) { setError(err.message) }
    else { onSuccess() }
    setSubmitting(false)
  }

  return (
    <div className="suggest-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="suggest-modal">
        <div className="suggest-modal-header">
          <div>
            <h3 className="suggest-modal-title">Suggest an Edit</h3>
            <p className="suggest-modal-source">Head Coach · {pipelineSchool.school_name} · CommitHQ schools database</p>
          </div>
          <button className="suggest-modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="suggest-modal-current">
          <p className="suggest-modal-section-label">Current data</p>
          <div className="suggest-modal-fields">
            <div className="suggest-modal-field">
              <span className="suggest-field-label">Name</span>
              <span className="suggest-field-value">{coach.first_name} {coach.last_name}</span>
            </div>
            <div className="suggest-modal-field">
              <span className="suggest-field-label">Title</span>
              <span className="suggest-field-value">{coach.title}</span>
            </div>
          </div>
        </div>

        <div className="suggest-modal-notes-section">
          <label className="suggest-modal-section-label" htmlFor="note-edit-notes">
            What needs to be updated?
          </label>
          <textarea
            id="note-edit-notes"
            className="suggest-modal-textarea"
            value={editNotes}
            onChange={e => { setEditNotes(e.target.value); setError(null) }}
            placeholder="Describe the correction…"
            rows={4}
            autoFocus
          />
        </div>

        {error && <div className="detail-error">{error}</div>}

        <div className="suggest-modal-actions">
          <button className="btn-cancel-coach" onClick={onClose}>Cancel</button>
          <button className="btn-save-coach" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit suggestion'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Toast ────────────────────────────────────────────────────────

function Toast({ message, onDone }) {
  const timerRef = useRef(null)

  useEffect(() => {
    timerRef.current = setTimeout(onDone, 3200)
    return () => clearTimeout(timerRef.current)
  }, [onDone])

  return (
    <div className="toast">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      {message}
    </div>
  )
}
